import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCustomerTierMutations } from '~/hooks/use-customer-tiers';
import { customerTierSchema, EMPTY_CUSTOMER_TIER, type CustomerTierFormValues } from '~/lib/schemas/customer-tier';
import { TierBullets } from './tier-bullets';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CustomerTierFormValues | null;
}

/** Add / edit a customer tier (Βαθμίδα Πελάτη). */
export function CustomerTierEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCustomerTierMutations();
	const form = useForm<CustomerTierFormValues>({
		resolver: zodResolver(customerTierSchema) as Resolver<CustomerTierFormValues>,
		defaultValues: initial ?? EMPTY_CUSTOMER_TIER,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);
	const chex = watch('chex');

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_CUSTOMER_TIER);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('customers.tiers.editTitle') : t('customers.tiers.addTitle')}</DialogTitle>
					<DialogDescription>{t('customers.tiers.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('customers.tiers.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</FormField>
						<FormField label={t('customers.tiers.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
					</div>
					<FormField label={t('customers.tiers.columns.description')} error={formState.errors.descr?.message}>
						<Textarea {...register('descr')} maxLength={500} rows={2} aria-invalid={Boolean(formState.errors.descr)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('customers.tiers.columns.level')} error={formState.errors.lvl?.message}>
							<Input type="number" {...register('lvl', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.lvl)} />
						</FormField>
						<FormField label={t('customers.tiers.columns.multiplier')} error={formState.errors.multiplier?.message}>
							<Input
								type="number"
								step="any"
								{...register('multiplier', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
								aria-invalid={Boolean(formState.errors.multiplier)}
							/>
						</FormField>
						<FormField label={t('customers.tiers.columns.purchaseAmountFrom')} error={formState.errors.paf?.message}>
							<Input
								type="number"
								step="0.01"
								min={0}
								{...register('paf', { setValueAs: v => (v === '' || v == null ? null : Number(v)) })}
								aria-invalid={Boolean(formState.errors.paf)}
							/>
						</FormField>
						<FormField label={t('customers.tiers.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<FormField label={t('customers.tiers.columns.color')} error={formState.errors.chex?.message}>
						<div className="flex items-center gap-2">
							<input
								type="color"
								value={chex || '#000000'}
								onChange={e => setValue('chex', e.target.value, { shouldDirty: true })}
								className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
								aria-label={t('customers.tiers.columns.color')}
							/>
							<Input
								value={chex ?? ''}
								onChange={e => setValue('chex', e.target.value || null, { shouldDirty: true })}
								placeholder="#RRGGBB"
								className="max-w-40"
							/>
						</div>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="ct-iv">{t('customers.tiers.columns.visible')}</Label>
							<Switch id="ct-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="ct-act">{t('customers.tiers.columns.active')}</Label>
							<Switch id="ct-act" checked={watch('act')} onCheckedChange={next => setValue('act', next, { shouldDirty: true })} />
						</div>
					</div>
					{isEdit && initial?.id ? <TierBullets tierId={initial.id} /> : null}
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
